import React, { useState, useEffect } from 'react';
import { 
  getAllProjects, 
  deleteProject, 
  Project,
  moveProjectUp,
  moveProjectDown,
  initializeProjectOrder 
} from '../../firebase/projectService';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProjectForm from '../../components/forms/ProjectForm';
import { Star, ChevronUp, ChevronDown } from 'lucide-react';

const ProjectsEditor: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const projectsData = await getAllProjects();
      setProjects(projectsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!id) return;
    
    try {
      await deleteProject(id);
      setProjects(projects.filter(project => project.id !== id));
      setDeleteConfirmationId(null);
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to delete project. Please try again.');
    }
  };

  const handleMoveUp = async (id: string) => {
    try {
      await moveProjectUp(id);
      await fetchProjects(); // Refresh the list
    } catch (err) {
      console.error('Error moving project up:', err);
      setError('Failed to move project up. Please try again.');
    }
  };

  const handleMoveDown = async (id: string) => {
    try {
      await moveProjectDown(id);
      await fetchProjects(); // Refresh the list
    } catch (err) {
      console.error('Error moving project down:', err);
      setError('Failed to move project down. Please try again.');
    }
  };



  const handleEdit = (id: string) => {
    setCurrentProjectId(id);
    setIsModalOpen(true);
  };

  const handleCreateNew = () => {
    setCurrentProjectId(null);
    setIsModalOpen(true);
  };

  const onFormSuccess = () => {
    setIsModalOpen(false);
    fetchProjects();
  };

  return (
    <div className="container py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Projects Editor</h1>
        <button
          onClick={handleCreateNew}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Create New Project
        </button>
      </div>

      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Manage your portfolio projects. Add, edit, or remove projects from your portfolio.
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {projects.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                You don't have any projects yet.
              </p>
              <button
                onClick={handleCreateNew}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Create Your First Project
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-6">
              {projects.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                >
                  <div className="flex flex-col lg:flex-row">
                    <div className="lg:w-1/4 h-48 lg:h-auto">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6 lg:w-3/4 flex flex-col justify-between">
                      <div>
                        <h2 className="text-xl font-bold mb-2">{project.title} {project.featured && (
                          <span className="inline-flex items-center ml-2 text-yellow-500" title="Featured on homepage">
                            <Star size={16} />
                          </span>
                        )}</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                          {project.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {project.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-md text-sm"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {/* Move buttons */}
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleMoveUp(project.id || '')}
                            className="px-2 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            title="Move up"
                          >
                            <ChevronUp size={16} />
                          </button>
                          <button
                            onClick={() => handleMoveDown(project.id || '')}
                            className="px-2 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            title="Move down"
                          >
                            <ChevronDown size={16} />
                          </button>
                        </div>
                        
                        <button
                          onClick={() => handleEdit(project.id || '')}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Edit
                        </button>
                        {deleteConfirmationId === project.id ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDelete(project.id || '')}
                              className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirmationId(null)}
                              className="px-3 py-1.5 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirmationId(project.id || '')}
                            className="px-3 py-1.5 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors"
                          >
                            Delete
                          </button>
                        )}
                        <Link
                          to={`/projects/${project.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                        >
                          View
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {isModalOpen && (
        <ProjectForm
          projectId={currentProjectId}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={onFormSuccess}
        />
      )}
    </div>
  );
};

export default ProjectsEditor; 